import { lazy, Suspense } from "react";
import { Layout } from "@/components/layout/Layout";
import { HeroSection } from "@/components/HeroSection";
import { QuickServicesStrip } from "@/components/QuickServicesStrip";
import PageSEO from "@/components/PageSEO";
import { PlaneLoader } from "@/components/PlaneLoader";

// Lazy load below-the-fold components for better LCP
const TrendingMarquee = lazy(() => import("@/components/TrendingMarquee").then(m => ({ default: m.TrendingMarquee })));
const FeaturesSection = lazy(() => import("@/components/FeaturesSection").then(m => ({ default: m.FeaturesSection })));
const TravelToolbox = lazy(() => import("@/components/TravelToolbox").then(m => ({ default: m.TravelToolbox })));
const MeetMatanSection = lazy(() => import("@/components/MeetMatanSection").then(m => ({ default: m.MeetMatanSection })));
const WallOfLoveSection = lazy(() => import("@/components/WallOfLoveSection").then(m => ({ default: m.WallOfLoveSection })));
const LeadCaptureSection = lazy(() => import("@/components/LeadCaptureSection").then(m => ({ default: m.LeadCaptureSection })));
const NewsletterSection = lazy(() => import("@/components/NewsletterSection").then(m => ({ default: m.NewsletterSection })));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup").then(m => ({ default: m.ExitIntentPopup })));

// CLS-safe skeleton with fixed height for each section type
const SectionSkeleton = ({ height = "py-16" }: { height?: string }) => (
  <div className={`${height} flex items-center justify-center bg-muted/20`}>
    <PlaneLoader />
  </div>
);

const Index = () => {
  return (
    <Layout>
      <PageSEO 
        title="מתכננים חופשה? הכלים החכמים למטייל"
        description="Travel Boost: תכנון טיול חכם עם מחשבון תקציב ל-195 מדינות, חידון יעדים, מדריכי נסיעות מקיפים, טיסות זולות, ביטוח נסיעות וחבילות eSIM. כל מה שצריך לחופשה מושלמת במקום אחד."
        keywords="מתכננים חופשה, כלים למטייל, תכנון טיול, מחשבון תקציב טיול, לאן לטוס, חידון יעדים"
      />
      {/* Critical above-the-fold content - loaded eagerly */}
      <HeroSection />
      <QuickServicesStrip />
      
      {/* Below-the-fold: lazy loaded with CLS-safe fixed-height skeletons */}
      <div className="render-lazy" style={{ minHeight: "80px" }}>
        <Suspense fallback={<SectionSkeleton height="min-h-[80px]" />}>
          <TrendingMarquee />
        </Suspense>
      </div>
      
      <div className="render-lazy-tall" style={{ minHeight: "400px" }}>
        <Suspense fallback={<SectionSkeleton height="min-h-[400px]" />}>
          <FeaturesSection />
        </Suspense>
      </div>
      
      <div className="render-lazy-tall" style={{ minHeight: "600px" }}>
        <Suspense fallback={<SectionSkeleton height="min-h-[600px]" />}>
          <TravelToolbox />
        </Suspense>
      </div>
      
      <div className="render-lazy" style={{ minHeight: "300px" }}>
        <Suspense fallback={<SectionSkeleton height="min-h-[300px]" />}>
          <MeetMatanSection />
        </Suspense>
      </div>
      
      <div className="render-lazy-tall" style={{ minHeight: "500px" }}>
        <Suspense fallback={<SectionSkeleton height="min-h-[500px]" />}>
          <WallOfLoveSection />
        </Suspense>
      </div>
      
      <div className="render-lazy" style={{ minHeight: "250px" }}>
        <Suspense fallback={<SectionSkeleton height="min-h-[250px]" />}>
          <LeadCaptureSection />
        </Suspense>
      </div>
      
      <div className="render-lazy" style={{ minHeight: "350px" }}>
        <Suspense fallback={<SectionSkeleton height="min-h-[350px]" />}>
          <NewsletterSection />
        </Suspense>
      </div>
      
      <Suspense fallback={null}>
        <ExitIntentPopup />
      </Suspense>
    </Layout>
  );
};

export default Index;

